import { DraggableKanbanBoard } from "./DraggableKanbanBoard";
import { getKanbanStageId, KanbanStage, REFUSED_MATCH_STATUSES } from "./types/index";
import { useKanbanColumns } from "@/hooks/useKanbanColumns";
import { useTranslation } from "react-i18next";
import { KanbanFilterBar } from "./KanbanFilterBar";
import { useState, useMemo, useRef, useCallback } from "react";
import { KanbanCandidate } from "./types";
import { useMatchMultiSelect } from "@/hooks/useMatchMultiSelect";
import { useSettings } from "@/providers/SettingsProvider";
import { MassUpdateModal } from "@/components/matches/MassUpdateModal";
import { MatchMultiSelectActions } from "@/components/matches/MatchMultiSelectActions";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface SharedKanbanBoardProps {
	jobId?: string;
	candidateId?: string;
	showAllMatches?: boolean;
	pointOfView?: "job" | "candidate";
	stages?: KanbanStage[];
	matches: KanbanCandidate[];
	onStageChange: (candidateId: string, newStage: string) => Promise<boolean | void> | boolean | void;
	onRefresh?: () => void;
	onMatchHired?: (match: KanbanCandidate) => void;
}

export const SharedKanbanBoard = ({
	jobId,
	candidateId,
	showAllMatches = false,
	stages,
	matches,
	onStageChange,
	onRefresh,
	onMatchHired,
}: SharedKanbanBoardProps) => {
	const { t } = useTranslation();
	const { stages: orgStages, columns: orgColumns, statusConfig } = useKanbanColumns();
	const effectiveStages = stages ?? orgStages;
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const { getDaysFilterForEntity, setDaysFilterForEntity } = useSettings();

	// Filter state
	const [searchQuery, setSearchQuery] = useState("");
	const [scoreFilter, setScoreFilter] = useState(0);
	const [maxDaysAgo, setMaxDaysAgoState] = useState<number | null>(() => getDaysFilterForEntity('matches'));

	const setMaxDaysAgo = useCallback((days: number | null) => {
		setMaxDaysAgoState(days);
		setDaysFilterForEntity('matches', days);
	}, [setDaysFilterForEntity]);

	// Multi-select state
	const [isMassUpdateModalOpen, setIsMassUpdateModalOpen] = useState(false);
	const [pendingRefusedDrop, setPendingRefusedDrop] = useState<{ candidateId: string } | null>(null);
	const [selectedRefusedStatus, setSelectedRefusedStatus] = useState("");

	const localizedStages = useMemo(() => effectiveStages.map(stage => ({
		...stage,
		name: t(`matching.statusLabels.${stage.id}`, stage.name)
	})), [effectiveStages, t]);

	// All configured column ids (hidden included) so matches sitting in a hidden
	// column disappear from the board instead of collapsing into "refused"
	const knownStageIds = useMemo(() => orgColumns.map(column => column.id), [orgColumns]);

	const refusedStatusOptions = useMemo(() => REFUSED_MATCH_STATUSES.map(status => ({
		value: status,
		label: t(`matching.statusLabels.${status}`)
	})), [t]);

	// Multi-select functionality
	const {
		isMultiSelectMode,
		toggleMultiSelect,
		toggleItemSelection,
		clearSelection,
		isSelected,
		selectedCount,
		selectAll,
		selectedItemIds
	} = useMatchMultiSelect(matches);

	// Filter matches based on search query, score, and days ago
	const filteredMatches = useMemo(() => {
		return matches.filter(match => {
			// Score filter
			if (match.score < scoreFilter) return false;

			// Days ago filter - filter by creation date
			if (maxDaysAgo !== null) {
				if (!match.createdAt) return false;
				const created = new Date(match.createdAt);
				const now = new Date();
				const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
				if (diffDays > maxDaysAgo) return false;
			}

			// Text search
			if (searchQuery.trim()) {
				const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
				
				// Get all searchable text fields in a single string
				const searchableText = [
					match.name,
					match.company,
					match.position,
					match.jobTitle,
					// Add any additional fields you want to search through
				]
					.filter(Boolean) // Remove null/undefined values
					.join(" ")
					.toLowerCase();

				// Check if ALL search terms are found in any of the fields
				return searchTerms.every(term => searchableText.includes(term));
			}

			return true;
		});
	}, [matches, searchQuery, scoreFilter, maxDaysAgo]);

	// Group filtered matches by their stage
	const candidatesByStage = localizedStages.reduce((acc, stage) => {
		acc[stage.id] = filteredMatches.filter(match => getKanbanStageId(match.stage, knownStageIds) === stage.id);
		return acc;
	}, {} as Record<string, typeof matches>);

	// Enhanced stage change handler that preserves scroll position
	const handleStageChangeWithScrollPreservation = async (candidateId: string, newStage: string) => {
		// Store current scroll position
		const scrollLeft = scrollContainerRef.current?.scrollLeft || 0;
		
		// Execute the original stage change
		const result = await onStageChange(candidateId, newStage);
		
		// Restore scroll position after a brief delay to allow re-render
		setTimeout(() => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollLeft = scrollLeft;
			}
		}, 0);

		return result;
	};

	const closeRefusedDialog = () => {
		setPendingRefusedDrop(null);
		setSelectedRefusedStatus("");
	};

	const handleCandidateDrop = async (matchId: string, targetColumnId: string) => {
		if (targetColumnId === 'refused') {
			setPendingRefusedDrop({ candidateId: matchId });
			return;
		}

		const result = await handleStageChangeWithScrollPreservation(matchId, targetColumnId);

		// After a successful hire, trigger the workforce creation callback
		if (targetColumnId === 'hired' && result !== false && onMatchHired) {
			const match = matches.find(m => m.id === matchId);
			if (match)
				onMatchHired(match);
		}
	};

	const handleConfirmRefusedStatus = async () => {
		if (!pendingRefusedDrop || !selectedRefusedStatus)
			return;

		const updateSucceeded = await handleStageChangeWithScrollPreservation(pendingRefusedDrop.candidateId, selectedRefusedStatus);
		if (updateSucceeded === false)
			return;

		closeRefusedDialog();
	};

	// Handle mass update completion
	const handleMassUpdateComplete = () => {
		setIsMassUpdateModalOpen(false);
		clearSelection();
		if (onRefresh) {
			onRefresh();
		}
	};

	return (
		<div className="w-full">
			<KanbanFilterBar
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				scoreFilter={scoreFilter}
				onScoreChange={(values) => setScoreFilter(values[0])}
				maxDaysAgo={maxDaysAgo}
				onMaxDaysAgoChange={setMaxDaysAgo}
			/>
			<div className="mb-4">
				<MatchMultiSelectActions
					selectedCount={selectedCount}
					isMultiSelectMode={isMultiSelectMode}
					onToggleMultiSelect={toggleMultiSelect}
					onMassUpdate={() => setIsMassUpdateModalOpen(true)}
					selectAll={selectAll}
				/>
			</div>
			<div ref={scrollContainerRef} className="w-full overflow-x-auto">
				<DraggableKanbanBoard
					stages={localizedStages}
					candidatesByStage={candidatesByStage}
					onCandidateDrop={handleCandidateDrop}
					pointOfView={candidateId ? "candidate" : "job"}
					statusConfig={statusConfig}
					isMultiSelectMode={isMultiSelectMode}
					onToggleItemSelection={toggleItemSelection}
					isItemSelected={isSelected}
				/>
			</div>

			<MassUpdateModal
				isOpen={isMassUpdateModalOpen}
				onClose={() => setIsMassUpdateModalOpen(false)}
				selectedMatchIds={selectedItemIds}
				onUpdate={handleMassUpdateComplete}
			/>

			<Dialog open={pendingRefusedDrop !== null} onOpenChange={(open) => {
				if (!open)
					closeRefusedDialog();
			}}>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('matching.refusedStatusModal.title')}</DialogTitle>
						<DialogDescription>{t('matching.refusedStatusModal.description')}</DialogDescription>
					</DialogHeader>

					<div className="space-y-2 py-2">
						<Label htmlFor="refused-status-select">{t('matching.refusedStatusModal.label')}</Label>
						<Select value={selectedRefusedStatus} onValueChange={setSelectedRefusedStatus}>
							<SelectTrigger id="refused-status-select">
								<SelectValue placeholder={t('matching.refusedStatusModal.placeholder')} />
							</SelectTrigger>
							<SelectContent>
								{refusedStatusOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={closeRefusedDialog}>
							{t('matching.refusedStatusModal.cancel')}
						</Button>
						<Button onClick={handleConfirmRefusedStatus} disabled={!selectedRefusedStatus}>
							{t('matching.refusedStatusModal.confirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};
